import type { RecordingStopReason } from '@/shared/api/transcriptor';

import type { RecordingEventName } from '@/entities/transcriptor';

export type AudioRecordingConfig = {
  chunkInterval: number;
  mimeType: string;
  audioBitsPerSecond: number;
  maxRetries: number;
  retryDelay: number;
};

export type RecordingState = {
  isRecording: boolean;
  isPaused: boolean;
  consultationId: string | null;
  fragmentId: string | null;
  chunkIndex: number;
  audioLevel: number;
};

export type AudioChunkMetadata = {
  consultationId: string;
  fragmentId: string;
  audioData: Blob;
  index: number;
  isLast: boolean;
  timestamp: number;
};

export type AudioChunkPayload = {
  audioData: Blob;
  index: number;
  isLast: boolean;
  // Meaningful on the last chunk only: the uploader sends it with /stop.
  stopReason?: RecordingStopReason;
};

// Capture-pipeline transitions that have no state of their own in the store
// and exist only to be reported; attribute keys are the telemetry contract.
export type RecordingDiagnostic = {
  name: Extract<
    RecordingEventName,
    | 'track_ended'
    | 'recorder_error'
    | 'ping_failed'
    | 'capture_suspended'
    | 'stop_requested'
    | 'chunk_upload_retry'
  >;
  attributes?: Record<string, string | number>;
};

export class AudioRecordingError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'AudioRecordingError';
  }
}

export type AudioAnalyzerConfig = {
  fftSize: number;
  smoothingTimeConstant: number;
  minDecibels: number;
  maxDecibels: number;
};

// Spelled out instead of the DOM `RecordingState` alias, which this package
// shadows with its own `RecordingState` type above.
export type RecorderState = 'inactive' | 'recording' | 'paused';

export type PingTelemetrySources = {
  track: MediaStreamTrack | null;
  recorderState: RecorderState | null;
  peakLevel: number | null;
  lastSignalTimestamp: number;
  chunkIndex: number;
  now: number;
  performanceNow: number;
  pageVisibility: DocumentVisibilityState | null;
};
